import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Grid,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";

import {
  CreateUserRequest,
  UpdateUserRequest,
  AdminUserDetail,
  UserModalMode,
  AvailableRole,
} from "../../../types/admin";
import {
  useAvailableRoles,
  useUserMutations,
} from "../../../hooks/useAdminUsers";
import { formatApiError } from "../../../utils/formatApiError";
import {
  validateCreateUser,
  validateUpdateUser,
  getFieldErrors,
  CreateUserFormData,
  UpdateUserFormData,
} from "../../../validation/adminUserSchema";
import { generateSecurePassword } from "../../../utils/passwordUtils";

interface UserFormModalProps {
  open: boolean;
  mode: UserModalMode;
  user?: AdminUserDetail;
  onClose: () => void;
  onSuccess: () => void;
}

type FormData = CreateUserFormData & { mode?: "create" | "edit" };

const UserFormModal: React.FC<UserFormModalProps> = ({
  open,
  mode,
  user,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    authType: "manual",
    isActive: true,
    isEmailVerified: false,
    roleIds: [],
    country: "",
    city: "",
    password: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string>("");

  // React Query hooks
  const { data: rolesData, isLoading: rolesLoading } = useAvailableRoles();
  const userMutations = useUserMutations();

  const availableRoles = rolesData?.data || [];
  const loading =
    userMutations.createUser.isPending || userMutations.updateUser.isPending;
  const [showPassword, setShowPassword] = useState(false);

  const isEditMode = mode === "edit";
  const isCreateMode = mode === "create";

  useEffect(() => {
    if (open) {
      if (isEditMode && user) {
        setFormData({
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phoneNumber: user.phoneNumber || "",
          authType: user.authType || "manual",
          isActive: user.isActive,
          isEmailVerified: user.isEmailVerified,
          roleIds: user.roles, // roles are already strings
          country: user.country || "",
          city: user.city || "",
          password: "",
        });
      } else if (isCreateMode) {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          authType: "manual",
          isActive: true,
          isEmailVerified: false,
          roleIds: [],
          country: "",
          city: "",
          password: "",
        });
      }
      setErrors({});
      setSubmitError("");
      setShowPassword(false); // Reset password visibility
    }
  }, [open, mode, user, isEditMode, isCreateMode]);

  const validateForm = (): boolean => {
    let validationResult;

    if (isCreateMode) {
      validationResult = validateCreateUser(formData);
    } else {
      // For update mode, include email but exclude password
      const updateData: UpdateUserFormData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email, // Include email for validation
        authType: formData.authType, // Include authType
        phoneNumber: formData.phoneNumber,
        country: formData.country,
        city: formData.city,
        isActive: formData.isActive,
        isEmailVerified: formData.isEmailVerified,
        roleIds: formData.roleIds,
      };
      validationResult = validateUpdateUser(updateData);
    }

    const newErrors = getFieldErrors(validationResult);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (isCreateMode) {
        const createRequest: CreateUserRequest = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email || "",
          phoneNumber: formData.phoneNumber,
          authType: formData.authType || "manual",
          isActive: formData.isActive,
          isEmailVerified: formData.isEmailVerified,
          country: formData.country,
          city: formData.city,
          password: formData.password,
          roleIds:
            formData.roleIds?.map((roleName) => {
              const role = availableRoles.find((r: AvailableRole) => r.name === roleName);
              return role ? role.id : roleName;
            }) || [],
        };
        await userMutations.createUser.mutateAsync(createRequest);
      } else if (isEditMode && user) {
        const updateRequest: UpdateUserRequest = {
          email: formData.email, // Include email in update request
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber,
          isActive: formData.isActive,
          isEmailVerified: formData.isEmailVerified,
          country: formData.country,
          city: formData.city,
          roleIds:
            formData.roleIds?.map((roleName) => {
              const role = availableRoles.find((r: AvailableRole) => r.name === roleName);
              return role ? role.id : roleName;
            }) || [],
        };
        await userMutations.updateUser.mutateAsync({
          userId: user.id,
          data: updateRequest,
        });
      }

      await onSuccess(); // Wait for the success callback to complete
    } catch (error: unknown) {
      const errorMessage = formatApiError(error);
      setSubmitError(errorMessage);
    }
  };

  const handleFieldChange = (field: string, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword(12);
    handleFieldChange("password", newPassword);
    setShowPassword(true); // Show the generated password
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const countries = [
    "United States",
    "Canada",
    "United Kingdom",
    "Germany",
    "France",
    "Italy",
    "Spain",
    "Netherlands",
    "Australia",
    "Japan",
    "Other",
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isCreateMode ? t("admin.userForm.createNewUser") : t("admin.userForm.editUser")}
      </DialogTitle>

      <DialogContent>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <Box mt={2}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("admin.userForm.basicInformation")}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("admin.userForm.firstName")}
                value={formData.firstName || ""}
                onChange={(e) => handleFieldChange("firstName", e.target.value)}
                error={!!errors.firstName}
                helperText={errors.firstName}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("admin.userForm.lastName")}
                value={formData.lastName || ""}
                onChange={(e) => handleFieldChange("lastName", e.target.value)}
                error={!!errors.lastName}
                helperText={errors.lastName}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("admin.userForm.email")}
                type="email"
                value={formData.email || ""}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t("admin.userForm.authType")}</InputLabel>
                <Select
                  value={formData.authType || "manual"}
                  onChange={(e) =>
                    handleFieldChange("authType", e.target.value)
                  }
                  label={t("admin.userForm.authType")}
                  disabled={isEditMode}
                >
                  <MenuItem value="manual">{t("admin.userForm.manualLocal")}</MenuItem>
                  <MenuItem value="google">{t("admin.userForm.google")}</MenuItem>
                  <MenuItem value="admin">{t("admin.userForm.admin")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {isCreateMode && formData.authType === "manual" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("admin.userForm.password")}
                  type={showPassword ? "text" : "password"}
                  value={formData.password || ""}
                  onChange={(e) =>
                    handleFieldChange("password", e.target.value)
                  }
                  error={!!errors.password}
                  helperText={errors.password}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={t("admin.userForm.togglePasswordVisibility")}>
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={togglePasswordVisibility}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("admin.userForm.generateSecurePassword")}>
                          <IconButton
                            aria-label="generate password"
                            onClick={handleGeneratePassword}
                            edge="end"
                            size="small"
                            color="primary"
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("admin.userForm.phoneNumber")}
                value={formData.phoneNumber || ""}
                onChange={(e) =>
                  handleFieldChange("phoneNumber", e.target.value)
                }
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                placeholder={t("admin.userForm.phoneNumberPlaceholder")}
              />
            </Grid>

            {/* Location */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("admin.userForm.location")}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={countries}
                value={formData.country || ""}
                onChange={(_, value) => handleFieldChange("country", value)}
                renderInput={(params) => (
                  <TextField {...params} label={t("admin.userForm.country")} />
                )}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("admin.userForm.city")}
                value={formData.city || ""}
                onChange={(e) => handleFieldChange("city", e.target.value)}
              />
            </Grid>

            {/* Roles */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("admin.userForm.rolesAndPermissions")}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              {rolesLoading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <Typography>{t("admin.userForm.loadingRoles")}</Typography>
                </Box>
              ) : (
                <Autocomplete
                  multiple
                  options={availableRoles}
                  getOptionLabel={(option) => option.name}
                  value={
                    availableRoles.filter((role: AvailableRole) =>
                      formData.roleIds?.includes(role.name),
                    ) || []
                  }
                  onChange={(_, value) =>
                    handleFieldChange(
                      "roleIds",
                      value.map((role: AvailableRole) => role.name),
                    )
                  }
                  renderTags={(value: AvailableRole[], getTagProps) =>
                    value.map((option: AvailableRole, index: number) => (
                      <Chip
                        variant="outlined"
                        label={option.name}
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("admin.userForm.roles")}
                      placeholder={t("admin.userForm.selectRoles")}
                    />
                  )}
                />
              )}
            </Grid>

            {/* Account Status */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("admin.userForm.accountStatus")}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive || false}
                    onChange={(e) =>
                      handleFieldChange("isActive", e.target.checked)
                    }
                  />
                }
                label={t("admin.userForm.activeAccount")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isEmailVerified || false}
                    onChange={(e) =>
                      handleFieldChange("isEmailVerified", e.target.checked)
                    }
                  />
                }
                label={t("admin.userForm.emailVerified")}
              />
            </Grid>
          </Grid>

          {Object.keys(errors).length > 0 && (
            <Box mt={2}>
              <Alert severity="error">
                {t("admin.userForm.fixErrorsBeforeSubmitting")}
              </Alert>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {isCreateMode ? t("admin.userForm.createUser") : t("admin.userForm.updateUser")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserFormModal;
